/**
 * The Traces tab's Metrics view: the metrics the project's services sent over OpenTelemetry (vfs-service
 * /telemetry/metrics). Pick a metric (the first one at first); each of its series (one per service and attribute set)
 * shows as a small line chart over the tab's time range, with its last, lowest and highest value. A counter shows its
 * increase between exports, a histogram the average of what it measured (vfs-service metricStore seriesView). Refreshed
 * every 5 s like the map, not while paused or the page is hidden.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getMetricSeries, listMetrics } from '../../services/TelemetryService';
import { REFRESH_MS, plural } from './tracesModel';
import { chartWords, formatValue, kindText, lineGeometry, seriesLabel, unitText, valueIsText } from './signalsModel';

const errorText = (error) => error?.body?.error || error?.message || String(error || 'Something went wrong');
const MAX_CHARTS = 8;
const WIDTH = 480;
const HEIGHT = 120;

function LineChart({ name, series, rangeLabel, from, to }) {
  const geo = lineGeometry(series.points, { width: WIDTH, height: HEIGHT, from, to });
  const s = series.summary || {};
  return (
    <figure className="traces-card traces-metric" data-testid="metrics-chart">
      <figcaption>
        <strong>{seriesLabel(series)}</strong>
        <span className="traces-muted-inline"> · {valueIsText(series.valueIs)}{unitText(series.unit) ? ` (${unitText(series.unit)})` : ''}</span>
      </figcaption>
      {geo.path ? (
        <svg aria-label={chartWords(name, series, rangeLabel)} className="traces-metric-chart" role="img" viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
          <polyline className="traces-metric-line" fill="none" points={geo.path} />
          {geo.dots.length <= 60 && geo.dots.map((d) => (
            <circle className="traces-metric-dot" cx={d.x} cy={d.y} key={d.t} r="2.5">
              <title>{`${new Date(d.t).toLocaleTimeString()}: ${formatValue(d.value, series.unit)}`}</title>
            </circle>
          ))}
        </svg>
      ) : <p className="traces-muted">No points in this range.</p>}
      <div className="traces-metric-summary" data-testid="metrics-summary">
        <span>Last <strong>{formatValue(s.last, series.unit)}</strong></span>
        <span>Lowest {formatValue(s.min, series.unit)}</span>
        <span>Highest {formatValue(s.max, series.unit)}</span>
        {s.samples !== undefined && <span>{plural(s.samples, 'measurement')}</span>}
      </div>
    </figure>
  );
}

LineChart.propTypes = {
  from: PropTypes.number,
  name: PropTypes.string.isRequired,
  rangeLabel: PropTypes.string.isRequired,
  series: PropTypes.object.isRequired,
  to: PropTypes.number,
};

export function MetricsEmpty() {
  return (
    <div className="traces-state" data-testid="metrics-empty">
      <strong>No metrics yet</strong>
      <p>
        Services send their metrics here through OpenTelemetry. Java services started by Flow do it on their own (request
        durations, every minute); in other stacks, use the OpenTelemetry SDK with its metrics exporter (OTLP): Flow already
        tells every service where to send them.
      </p>
    </div>
  );
}

export default function MetricsView({ projectId, range, rangeLabel, paused = false }) {
  const [metrics, setMetrics] = useState(null);
  const [picked, setPicked] = useState('');
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);
  const seq = useRef(0);

  const loadNames = useCallback(async () => {
    try {
      const page = await listMetrics(projectId);
      setMetrics(page);
      setError(null);
      const names = (page?.metrics || []).map((m) => m.name);
      setPicked((current) => (current && names.includes(current) ? current : (names[0] || '')));
    } catch (e) {
      setError(e);
    }
  }, [projectId]);

  const loadSeries = useCallback(async () => {
    if (!picked) { setAnswer(null); return; }
    const mine = ++seq.current;
    try {
      const page = await getMetricSeries(projectId, { name: picked, since: range });
      if (mine === seq.current) { setAnswer(page); setError(null); }
    } catch (e) {
      if (mine === seq.current) { setError(e); }
    }
  }, [projectId, picked, range]);

  useEffect(() => { loadNames(); }, [loadNames]);
  useEffect(() => { loadSeries(); }, [loadSeries]);

  useEffect(() => {
    if (paused) { return undefined; }
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) { return; }
      loadNames();
      loadSeries();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadNames, loadSeries, paused]);

  if (error && !metrics) {
    return (
      <div className="traces-state is-bad" role="alert">
        <strong>Could not load the metrics</strong>
        <p>{errorText(error)}</p>
        <button className="traces-btn" onClick={() => loadNames()} type="button">Try again</button>
      </div>
    );
  }
  if (!metrics) { return <div className="traces-state" role="status">Loading the metrics…</div>; }
  if (metrics.enabled === false) {
    return (
      <div className="traces-state">
        <strong>Tracing is off for this sandbox</strong>
        <p>{metrics.reason || 'The services of this sandbox do not send metrics.'}</p>
      </div>
    );
  }
  const list = metrics.metrics || [];
  if (!list.length) { return <section className="traces-card"><MetricsEmpty /></section>; }

  const metric = list.find((m) => m.name === picked) || null;
  const series = answer?.name === picked ? (answer.series || []) : [];
  return (
    <section aria-labelledby="metrics-title" className="traces-metrics" data-testid="metrics-view">
      <div className="traces-list-head">
        <h2 id="metrics-title">Metrics <span className="traces-muted-inline">· {plural(list.length, 'metric')}</span></h2>
        <label className="traces-sr" htmlFor="metrics-pick">Metric</label>
        <select className="traces-btn traces-mono" id="metrics-pick" onChange={(e) => setPicked(e.target.value)} value={picked}>
          {list.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
        </select>
        {metric && (
          <span className="traces-muted" data-testid="metrics-about">
            {kindText(metric)} · {metric.services.join(', ')}{metric.series > 1 ? ` · ${plural(metric.series, 'series', 'series')}` : ''}
          </span>
        )}
      </div>
      {error && <p className="traces-muted" role="status">Could not refresh: {errorText(error)}</p>}
      {answer?.name === picked && !series.length && <p className="traces-muted">No points of {picked} {rangeLabel.toLowerCase()}.</p>}
      <div className="traces-metric-grid">
        {series.slice(0, MAX_CHARTS).map((s) => (
          <LineChart
            from={answer.since}
            key={`${s.service}|${JSON.stringify(s.attributes)}`}
            name={picked}
            rangeLabel={rangeLabel}
            series={s}
            to={answer.until}
          />
        ))}
      </div>
      {answer?.total > MAX_CHARTS && <p className="traces-muted">The {MAX_CHARTS} series updated last of {answer.total}.</p>}
    </section>
  );
}

MetricsView.propTypes = {
  paused: PropTypes.bool,
  projectId: PropTypes.string.isRequired,
  // The tab's time range ('15m', '1h', …: tracesModel RANGES) and its label ("Last 15 minutes").
  range: PropTypes.string.isRequired,
  rangeLabel: PropTypes.string.isRequired,
};
